'use client';

import { trackedEntity } from '@cineamo/pulse';
import { usePulseTracker } from '@cineamo/pulse/react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback } from 'react';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { addUtmParams } from '@/helpers/utm-helper';
import { copyToClipboard } from '@cineamo/ui/src/helpers/clipboard-helper';

import { IconShare16 } from '@cineamo/ui/src/icons/ic-share';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const ShareShowingLinkButton: React.FC<{
    showing: ShowingDto;
    onShare: () => void;
    movieId?: number;
    hideText?: boolean;
}> = ({ showing, onShare, movieId, hideText }) => {
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();
    const { track: pulseTrack } = usePulseTracker();

    const copyShowingDetailLink = useCallback(async () => {
        const link = addUtmParams(`${window.location.origin}/program/${showing.contentId}/${showing.id}`, {
            utmSource: 'cinema_website',
            utmContent: `share_showing-${showing.id}-${showing.name}`,
            utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
            utmMedium: 'website'
        });
        copyToClipboard(link, t('showing-info.copied'));
    }, [showing, t, domainConfiguration.cinema.id, domainConfiguration.cinema.name]);

    const shareShowingDetailLink = useCallback(async () => {
        const link = `${window.location.origin}/program/${showing.contentId}/${showing.id}`;
        const shareData = {
            url: link
        };
        if (!navigator.canShare?.(shareData)) {
            copyShowingDetailLink();
            return;
        }
        await navigator
            .share(shareData)
            .then(() => {
                onShare();
                pulseTrack({
                    ...trackedEntity({ movieId, contentId: showing.contentId }),
                    event_type: 'share',
                    context: { showing_id: showing.id }
                });
            })
            .catch(() => {});
    }, [showing, copyShowingDetailLink, onShare, movieId, pulseTrack]);

    return (
        <button
            className="flex row items-center justify-center space-x-4 px-12 py-6 leading-[24px] text-body-bold-16 text-[var(--on-surface-color)]"
            onClick={() => {
                shareShowingDetailLink();
            }}
            aria-label={t('showing-info.share')}>
            <IconShare16 />
            {!hideText && <span>{t('showing-info.share')}</span>}
        </button>
    );
};

export default ShareShowingLinkButton;
